/* eslint-disable max-lines */
import { QUARTERS_ENDING_MONTHS, TURN_PHASES } from './constants';
// ---- local imports ---- 

export function get(obj, key) {
	return key.split('.').reduce(function (o, x) {
		return (typeof o == 'undefined' || o === null) ? o : o[x];
	}, obj);
}

export function isNull(input) {
	return [null, 'null', 'NULL', 'nil'].includes(input);
}

export function isEmptyString(text) {
	return text === undefined || text === null || text.trim() === '';
}

export function getLocalStore(key) {
	const item = localStorage.getItem(key);
	return JSON.parse(item);
}

export function setLocalStore(key, value) {
	localStorage.setItem(key, JSON.stringify(value));
}

export function shouldSkipCustomerAnimations() {
	return (isDevEnv() || isTestEnv()) && getLocalStore('dev_tools_skip_customer_animations') === true;
}

export function setSkipCustomerAnimations(shouldSkip) {
	if (!isDevEnv() && !isTestEnv()) return;
	setLocalStore('dev_tools_skip_customer_animations', shouldSkip);
}

export function getBaseUrl() {
	return process.env.REACT_APP_API_BASE_URL;
}

export function formatCash(cash) {
	if (cash === undefined || cash === null)
		return 0;

	return (cash + '').replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

export function removeNewLineCharacters(str) {
	return str.replace(/\\n/g, '');
}

export function getRandomInt(max) {
	return Math.floor(Math.random() * Math.floor(max));
}

export function getRandomIntInRange(min, max) {
	return getRandomInt(max) + min;
}

export function getIntZeroIfNaN(val) {
	return isNaN(val) ? 0 : val;
}

export function asInt(val) {
	return (
		isNaN(val)
		|| val === undefined
		|| val === null
		|| (val.trim && val.trim() === '') 
	)
		? 0
		: parseInt(val);
}

export function asPositiveInt(val) {
	const intNum = asInt(val);
	return intNum > 0 ? intNum : 0;
}

export function roundToNearest(x, base, useCeil = false) {
	return useCeil
		? Math.ceil(x / base) * base
		: Math.floor(x / base) * base;
}

export function getMonthFullName(date) {
	return date ? new Date(date).toLocaleDateString(undefined, { month: 'long' }) : '';
}

export function getShortMonthAndYear(date) {
	if (date === undefined || date === null)
		return '';

	const dateObj = new Date(date);

	const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
	const year = dateObj.getFullYear().toString().substr(-2);
	return `${month} ${year}`;
}

export function getFullMonthAndYear(date) {
	if (date === undefined || date === null)
		return '';

	const dateObj = new Date(date);
	const month = dateObj.toLocaleDateString('en-US', { month: 'long' });

	return `${month} ${dateObj.getFullYear()}`;
}

export function formatDate(date) {
	if (date === undefined || date === null)
		return '';

	const dateObj = new Date(date);

	const day = dateObj.getDate();
	const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
	const year = dateObj.getFullYear().toString().substr(-2);
	return `${day} ${month} ${year}`;
}

export function turnToLogbookDate(turn) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const year = new Date().getFullYear() + Math.floor((turn - 1) / 12) - 2000;
    return `${months[(turn - 1) % 12]} ${year}`;
}

export function formatDateForLogbook(date) {
	if (date === undefined || date === null)
		return '';

	const dateObj = new Date(date);

	const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
	const year = dateObj.getFullYear().toString().substr(-2);
	return `${month} ${year}`;
}

/**
 * disable all keys for numerical input field expect 
 * numbers, backspace, up and down keys and minus sign if allowNegative is true
 * 	
 * @param {KeyboardEvent} e 
 * @param {boolean} allowNegative 
 * @returns void
 */
export function disableInvalidInputKey(e, allowNegative = false) {
	// Allow shortcuts (Ctrl+C, Ctrl+V, Cmd+C, Cmd+V etc.)
	if (e.ctrlKey || e.metaKey) {
		return;
	}

	const isNumericKey = ((e.keyCode >= 48 && e.keyCode <= 57)
		|| (e.keyCode >= 96 && e.keyCode <= 105));
	const isBackspace = e.keyCode === 8;
    const isTab = e.keyCode === 9;
	const isHorizontalArrowKey = e.keyCode === 37 || e.keyCode === 39;
	const isNegative = e.keyCode === 189 || e.keyCode === 109;

	if (!isNumericKey
		&& !isBackspace
        && !isTab
		&& !isHorizontalArrowKey
		&& !(isNegative && allowNegative)
	) {
		e.preventDefault();
	}
}

/**
 * Return random int value between min and max and not in provided array of numbers
 * 
 * @param {int} min 
 * @param {int} max 
 * @param {array} exclude 
 */
export function getRandomIntInRangeNotIn(min, max, exclude) {
	let possibleValue = null;
	do {
		possibleValue = getRandomIntInRange(min, max);
	} while (exclude.includes(possibleValue));

	return possibleValue;
}

export function capitalizeFirstLetter(str) {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

export function turnToTurnWithinQuarter(currentDate, currentTurnPhase) {
	const currentMonth = new Date(currentDate).getMonth() % 3;
	return currentTurnPhase === TURN_PHASES.PREPARATION
		? (currentMonth * 2) + 1
		: (currentMonth * 2) + 2;
}

export function turnToMonth(turn) {
	const months = ['January', 'Febuary', 'March', 'April', 'May', 'June',
		'July', 'August', 'September', 'October', 'November', 'December'];
	return months[(turn - 1) % 12];
}

export function turnToShortMonth(turn) {
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
		'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	return months[(turn - 1) % 12];
}

export function turnToShortDate(turn) {
	const currentMonth = turnToShortMonth(turn);
	const currentYear = (new Date().getFullYear() + Math.floor((turn - 1) / 12));
	const shortYear = currentYear.toString().slice(-2);
	return `${currentMonth} ${shortYear}`;
}

export function convertToShortAddress(address) {
	if (!address)
		return
	address = address.replace('Street', 'St');

	const n = address.indexOf('.');
	if (n !== -1) {
		address = address.substr(0, n + 1);
	}

	return address;
}

export function disableKeyboardZoom(event) {
	const controlDown = event.ctrlKey || event.metaKey;

	if (controlDown && (event.key === '=' || event.key === '-' || event.key === '+')) {
		event.preventDefault();
	}
}

export function disableMouseWheelZoom(event) {
	if (event.ctrlKey || event.metaKey) { 
	  event.preventDefault();
	}
}

export function disablePinchZoom(event) {
	if (event.touches.length > 1) {
		event.preventDefault();
	}
}

export function getDateFrom(day, month, year) {
	day = day === 1 
		? '01'
		: day.toString();

	month = month > 9 
		? month.toString()
		: '0' + month.toString();

	year = year.toString().slice(-2);

	return `${day}/${month}/${year}`;
}

export function isTestEnv() {
	return window.Cypress !== undefined;
}

export function isMissionsFailureDisabled() {
	return process.env.REACT_APP_DISABLE_MISSION_FAILURE ?? false;
}

export function getMonthsDifference(startDate, endDate) {
	const s = new Date(startDate);
	const e = new Date(endDate);

	return e.getMonth() - s.getMonth()
		+ (12 * (e.getFullYear() - s.getFullYear()));
}

export function isDateAfter(date, targetDate) {
	const d = new Date(date);
	const t = new Date(targetDate);

	return d > t;
}

export function isDateBetween(date, fromDate, toDate, inclusive = true) {
	const d = new Date(date),
		from = new Date(fromDate),
		to = new Date(toDate);
		
	if(inclusive) {
		return d >= from && d <= to;
	}

	return d > from && d < to ;
}

export function isLastMonthInQuarter(monthStr) {
	return QUARTERS_ENDING_MONTHS.includes(monthStr);
}

export function safeDivision(numerator, denominator, decimalPlaces = 1) {
	if (typeof denominator !== 'number') {
		return undefined;
	}

	if (denominator === 0) {
		return 'N/A';
	}

	if (numerator === 0) {
		return 0;
	}

	return !numerator ? 0 : (numerator / denominator).toFixed(decimalPlaces);
}

export function getElementTransformPosition(element) {
	if (!(element && element instanceof Element)) {
		console.warn('htmlElement is not defined or not an Element');
		return;
	}

	const domMatrix = new WebKitCSSMatrix(window.getComputedStyle(element).transform);

	return {
		x: domMatrix.m41,
		y: domMatrix.m42,
	};
}

export function isDevEnv() {
	return process.env.NODE_ENV === 'development' 
	|| process.env.NODE_ENV === 'test';
}

/**
 * Helper to normalize input string when previous value was 0.
 * Handles cases like "05" -> "5" and "50" (inserted before 0) -> "5".
 */
export function normalizeZeroPlaceholder(valueString, prevValue) {
    if (prevValue !== 0) return valueString;

    if (valueString.length > 1) {
        if (valueString.startsWith('0')) {
            return valueString.substring(1);
        } else if (valueString.endsWith('0')) {
            return valueString.slice(0, -1);
        }
    }
    return valueString;
}

/**
 * Standardized logic for processing numeric input fields.
 * Handles "zeros as placeholders", negative numbers, and Min/Max constraints.
 * 
 * @param {string} newValue - The raw value from the input event.
 * @param {number} prevValue - The previous state value (number).
 * @param {number} min - Minimum allowed value.
 * @param {number} max - Maximum allowed value.
 * @param {boolean} allowNegative - Whether to allow negative numbers.
 * @returns {number|string} - The processed value (number) or intermediate string (e.g. '-')
 */
export function processNumericInput(newValue, prevValue, min, max, allowNegative = false) {
	// 1. Handle completely empty input -> return 0
	if (newValue === '' || newValue === '$') return 0;

	// 2. Handle standalone signs if allowing negative
	if (allowNegative && (newValue === '-' || newValue === '-0')) {
		return '-';
	}

	// 3. Clean non-numeric characters (except minus if allowed)
	const cleanRegex = allowNegative ? /[^0-9-]/g : /[^0-9]/g;
	let cleaned = newValue.toString().replace(cleanRegex, '');
	
	if (cleaned === '' || cleaned === '-') return allowNegative && cleaned === '-' ? '-' : 0;

	// 4. Zero Placeholder Logic
    cleaned = normalizeZeroPlaceholder(cleaned, prevValue);

	// 5. Parse Integer
	let parsed = parseInt(cleaned, 10);
	if (isNaN(parsed)) return 0;

	// 6. Constraints
	if (max !== undefined && parsed > max) parsed = max;
	if (min !== undefined && parsed < min) parsed = min;
	
    // 7. Prevent negative if not allowed
    if (!allowNegative && parsed < 0) parsed = 0;

	return parsed;
}

/**
 * Helper to process monetary inputs (Financial Statements, Ratios).
 * Handles currency symbols, negative formatting, and zero placeholders.
 * 
 * @param {string} newValue - The raw input string.
 * @param {number} prevValue - The previous numeric value.
 * @param {number} min - Minimum limit.
 * @param {number} max - Maximum limit.
 * @returns {number|string} - The processed number or '-' if intermediate negative input.
 */
export function processMonetaryInput(newValue, prevValue, min, max) {
	// 1. Handle completely empty input -> return 0
	if (newValue === '' || newValue === '$') return 0;
	
    // 2. Check for Negative Intent
    const hasMinus = newValue.includes('-');

	// 3. Clean non-digit characters (remove minus for parsing)
	const cleaned = newValue.toString().replace(/[^0-9]/g, '');
	
    if (cleaned === '') {
        // If we have just a minus (or mess of symbols with minus), return '-'
        return hasMinus ? '-' : 0;
    }

	// 4. Zero Placeholder Logic (on digits only)
    const processedString = normalizeZeroPlaceholder(cleaned, prevValue);

	// 5. Parse Integer
	let parsed = parseInt(processedString, 10);
	if (isNaN(parsed)) return 0;
    
    // 6. Handle Negative Logic
    if (hasMinus) {
        if (prevValue === 0) {
            // Context: User editing "0".
            // We allow ANY minus presence to turn it negative.
            // Ex: "$0-" -> "-"
            // Ex: "-$0" -> "-"
            // Ex: "$0-5" -> "-5"
            // If the numeric result is 0, we must return '-' to show intent.
            if (parsed === 0) return '-';
            parsed = -Math.abs(parsed);
        } else {
            // Context: User editing non-zero value.
            // STRICT validation. Minus must be at start of the INPUT string (ignoring currency symbol if possible, but easier to check raw).
            // Actually, comparing raw newValue is tricky because `$`.
            
            // Check formatted string:
            // "50-5" -> Invalid.
            // "-50" -> Valid.
            
            // Allow if minus count == 1 and it's physically the first meaningful char?
            // " -$50" ?
            // Simplest strict check: does the sanitized "digits-and-minus" string start with minus?
             const digitsAndMinus = newValue.toString().replace(/[^0-9-]/g, '');
             if (digitsAndMinus.startsWith('-') && (digitsAndMinus.match(/-/g) || []).length === 1) {
                 parsed = -Math.abs(parsed);
             } else {
                 // Invalid negative edit (e.g. 50-), ignore change
                 return prevValue;
             }
        }
    }

	// 7. Constraints
	if (max !== undefined && parsed > max) parsed = max;
	if (min !== undefined && parsed < min) parsed = min;
	
	return parsed;
}

/**
 * Counts the number of significant characters (matching regex) before a given position.
 * Useful for maintaining cursor position across format changes.
 * 
 * @param {string} text - The text to analyze.
 * @param {number} position - The cursor position (index) to count up to.
 * @param {RegExp} regex - Regex to identify significant characters (default: /\d/).
 * @param {function} shouldSkipChar - Optional callback (text, index) => boolean to skip specific chars.
 * @returns {number} The count of significant characters.
 */
export function countSignificantChars(text, position, regex = /\d/, shouldSkipChar = null) {
	let count = 0;
	if (!text) return 0;
	
	const limit = Math.min(position, text.length);
	for (let i = 0; i < limit; i++) {
		if (regex.test(text[i])) {
			if (shouldSkipChar && shouldSkipChar(text, i)) {
				continue;
			}
			count++;
		}
	}
	return count;
}

/**
 * Sets the cursor position in an input element based on a target count of significant characters.
 * Uses requestAnimationFrame to ensure the DOM has updated with the formatted text.
 * 
 * NOTE: This function does NOT call element.focus(), as it is intended for use in onChange handlers
 * where the input already has focus. Calling focus() here might interfere with mobile keyboards or rapid typing.
 * 
 * @param {HTMLInputElement} element - The input element.
 * @param {string} formattedText - The new formatted text value of the input.
 * @param {number} targetCount - The number of significant characters that should be before the cursor.
 * @param {RegExp} regex - Regex to identify significant characters (default: /\d/).
 */
export function setCursorToSignificantPosition(element, formattedText, targetCount, regex = /\d/) {
	requestAnimationFrame(() => {
		if (!element) return;
		
		let newCursorPos = 0;
		let currentCount = 0;
		const text = formattedText || '';
		
		for (let i = 0; i < text.length; i++) {
			if (regex.test(text[i])) {
				currentCount++;
				if (currentCount === targetCount) {
					newCursorPos = i + 1;
					break;
				}
			}
		}

		// Fallback: If we didn't find enough digits to match the previous position, place cursor at the end
		if (currentCount < targetCount) {
			newCursorPos = text.length;
		}

		element.setSelectionRange(newCursorPos, newCursorPos);
	});
}

/**
 * Simple wrapper to set cursor position via requestAnimationFrame.
 * Useful for components where position is calculated explicitly (e.g. Calculator).
 * 
 * NOTE: This function EXPLICITLY calls element.focus(). This is required for virtual keypad interactions
 * where the input might lose focus (e.g. clicking a button), but is generally not needed for direct typing.
 * 
 * @param {HTMLInputElement} element 
 * @param {number} position 
 */
export function setCursorPosition(element, position) {
	requestAnimationFrame(() => {
		if (element) {
			element.focus();
			element.setSelectionRange(position, position);
		}
	});
}
